import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import apiClient from "@dashboard/files/api/apiClient";
import { ChangesDialog } from "@dashboard/shared/components/ChangesDialog";
import { useCurrentSubscriptionExpired } from "@dashboard/workspaces/api/helpers";
import { TableRow } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showErrorToast } from "@shared/toasts/Toasts";
import { flexRender, Row } from "@tanstack/react-table";

import { InformationModal } from "./InformationModal";

const useStyles = makeStyles()((theme) => ({
    actionContainer: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
}));

interface TableItemProps {
    row: Row<API.ReleaseFile>;
    index: number;
}
const TableItem = ({ row }: TableItemProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isChangesDialogOpen, setIsChangesDialogOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const { t } = useTranslation(["pages", "common", "modals"]);
    const { classes } = useStyles();
    const isExpired = useCurrentSubscriptionExpired();

    const cannotDownload = isExpired && !row.original.isPublic;

    const handleDownload = async () => {
        try {
            setLoading(true);
            await apiClient.invokeFileDownload(row.original.id, row.original.name ?? "", true);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <TableRow>
                {row
                    .getVisibleCells()
                    .map((cell) => flexRender(cell.column.columnDef.cell, { ...cell.getContext(), key: cell.id }))}
                <MinimalTableCell style={{ maxWidth: 150 }}>
                    <div className={classes.actionContainer}>
                        <PrimaryButton
                            name="download-file"
                            loading={loading}
                            disabled={loading || cannotDownload}
                            onClick={handleDownload}
                            tooltipProps={{
                                title: t("pages:downloadsPage.tooltips.downloadFiles"),
                            }}
                        >
                            <i className="fa-solid fa-download" />
                        </PrimaryButton>
                        <SecondaryButton
                            name="show-changes"
                            tooltipProps={{
                                title: t("pages:downloadsPage.tooltips.showChanges"),
                            }}
                            onClick={() => setIsChangesDialogOpen(true)}
                        >
                            <i className="fas fa-list" />
                        </SecondaryButton>
                        <SecondaryButton
                            tooltipProps={{ title: t("pages:downloadsPage.tooltips.showDetails") }}
                            onClick={() => setIsModalOpen(true)}
                            name="show-details"
                        >
                            <i className="fas fa-info-circle" />
                        </SecondaryButton>
                    </div>
                </MinimalTableCell>
            </TableRow>
            <InformationModal file={row.original} isOpen={isModalOpen} toggleDialog={() => setIsModalOpen(false)} />
            <ChangesDialog
                title={t("modals:changesDialog.titles.downloads")}
                onClose={() => setIsChangesDialogOpen(false)}
                changesWithDate={row.original.changesWithDate}
                open={isChangesDialogOpen}
            />
        </React.Fragment>
    );
};

export default TableItem;
